import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BROADCAST_EXCLUSION_LABELS,
  estimateBroadcastCost,
  formatTemplateTotal,
  mentionsOptOut,
  templateCategoryLabel,
  type BroadcastFilters,
  type BroadcastVariableMapping,
  type WabaTemplate,
  type WabaTemplateMedia,
} from "@school-sales-ai/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TemplatePreview } from "@/components/templates/TemplatePreview";
import { BroadcastVariableMapper } from "@/components/broadcasts/BroadcastVariableMapper";
import { BroadcastFilterChips } from "@/components/broadcasts/BroadcastFilterChips";
import { BroadcastCostNote } from "@/components/broadcasts/BroadcastCostNote";
import { HeaderMediaPicker } from "@/components/templates/HeaderMediaPicker";
import {
  useBroadcastPreview,
  useCreateBroadcast,
  useTemplateVariables,
} from "@/hooks/useBroadcasts";
import { useUploadTemplateMedia, useWabaTemplates } from "@/hooks/useWabaTemplates";

type Step = 1 | 2 | 3;

const QUALITY_WARNING: Record<string, string> = {
  YELLOW: "Calidad media en Meta — un envío masivo puede empeorarla.",
  RED: "Calidad baja en Meta. Un envío masivo puede provocar que pausen tu número.",
};

export function BroadcastDialog({
  open,
  onClose,
  filters,
}: {
  open: boolean;
  onClose: () => void;
  filters: BroadcastFilters;
}): React.JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [mapping, setMapping] = useState<BroadcastVariableMapping>({});

  const { data: templates } = useWabaTemplates(false);
  const sendable = useMemo(
    () =>
      (templates ?? []).filter(
        (t) => t.status === "APPROVED" && !t.archivedAt && !t.metaDeletedAt,
      ),
    [templates],
  );
  const template = sendable.find((t) => t.id === templateId) ?? null;
  const { data: requirements } = useTemplateVariables(templateId);
  const variables = requirements?.variables;
  const header = requirements?.header;

  // The file for THIS send: the template's own by default, replaceable here.
  // A school that always mails the same brochure never re-uploads it.
  const [headerMedia, setHeaderMedia] = useState<WabaTemplateMedia | null>(null);
  const [headerFileName, setHeaderFileName] = useState("");
  const chosenMedia = headerMedia ?? header?.defaultMedia ?? null;

  const mappingComplete = (variables ?? []).every((token) => {
    const binding = mapping[token];
    if (!binding) return false;
    return binding.source === "lead_field" || binding.value.trim() !== "";
  });
  // A media-header template sent without its file is refused by Meta for every
  // recipient, so "ready" has to include it.
  const headerSatisfied = !header?.requiresMedia || chosenMedia !== null;

  const { data: preview, isPending: previewPending } = useBroadcastPreview({
    filters,
    templateId: templateId ?? undefined,
    mapping: mappingComplete ? mapping : undefined,
    enabled: open && !!templateId && mappingComplete,
  });

  const create = useCreateBroadcast();
  const uploadMedia = useUploadTemplateMedia();

  function reset(): void {
    setStep(1);
    setTemplateId(null);
    setMapping({});
    setHeaderMedia(null);
    setHeaderFileName("");
  }

  function handleClose(): void {
    reset();
    onClose();
  }

  const eligibleCount = preview?.eligible.length ?? 0;
  // Estimated locally so the number moves with the mapping instead of waiting
  // for a round trip; it is the SAME shared function the backend snapshots.
  const estimate = template ? estimateBroadcastCost(template.category, eligibleCount) : null;
  const first = preview?.eligible[0];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {step === 1
              ? "Plantilla y datos"
              : step === 2
                ? "Destinatarios"
                : "Confirmar envío"}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Elige una plantilla aprobada y define con qué se llena cada dato."
              : step === 2
                ? "Estos son los prospectos que coinciden con los filtros actuales."
                : "Revisa el mensaje y confirma. Meta cobra cada mensaje entregado."}
          </DialogDescription>
        </DialogHeader>

        {/* ── Step 1 ─────────────────────────────────────────────────── */}
        {step === 1 ? (
          <div>
            {sendable.length === 0 ? (
              <p className="rounded-md bg-ink-050 p-4 text-sm text-ink-700">
                No hay plantillas aprobadas. Se crean y se envían a revisión en
                Plantillas.
              </p>
            ) : (
              <div className="space-y-2">
                {sendable.map((t: WabaTemplate) => (
                  <label
                    key={t.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                      templateId === t.id
                        ? "border-moss bg-lime-pale"
                        : "border-ink-100 hover:border-ink-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="template"
                      className="mt-1"
                      checked={templateId === t.id}
                      onChange={() => {
                        setTemplateId(t.id);
                        setMapping({});
                        // Another template means another header, and possibly
                        // another KIND of file.
                        setHeaderMedia(null);
                        setHeaderFileName("");
                      }}
                    />
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-ink-900">{t.name}</span>
                        <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-700">
                          {templateCategoryLabel(t.category)}
                        </span>
                      </span>
                      <span className="mt-0.5 block line-clamp-2 text-sm text-ink-700">
                        {t.body}
                      </span>
                      {/* Quality is the leading indicator of a number being
                          paused, and a blast is the fastest way to spend it. */}
                      {t.qualityScore && QUALITY_WARNING[t.qualityScore] ? (
                        <span className="mt-1 block text-xs text-amber-700">
                          {QUALITY_WARNING[t.qualityScore]}
                        </span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {template ? (
              <>
                {header?.requiresMedia ? (
                  <div className="mt-4 rounded-md border border-ink-200 p-3">
                    <p className="text-sm font-medium text-ink-800">
                      {header.format === "IMAGE"
                        ? "Imagen del encabezado"
                        : "Documento del encabezado"}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-500">
                      Se envía el mismo archivo a todos los destinatarios.
                    </p>
                    <HeaderMediaPicker
                      kind={header.format === "IMAGE" ? "image" : "document"}
                      media={chosenMedia}
                      isUploading={uploadMedia.isPending}
                      onPick={(file) =>
                        uploadMedia.mutate(
                          {
                            file,
                            kind: header.format === "IMAGE" ? "image" : "document",
                          },
                          {
                            onSuccess: (media) => {
                              setHeaderMedia(media);
                              // A new file resets the displayed name, so the
                              // prospect never gets the previous file's name.
                              setHeaderFileName("");
                            },
                          },
                        )
                      }
                    />
                    {header.format === "DOCUMENT" && chosenMedia ? (
                      <div className="mt-3">
                        <label
                          className="text-sm font-medium text-ink-800"
                          htmlFor="broadcast-header-filename"
                        >
                          Nombre del archivo
                        </label>
                        <input
                          id="broadcast-header-filename"
                          type="text"
                          maxLength={255}
                          value={headerFileName || chosenMedia.fileName}
                          onChange={(e) => setHeaderFileName(e.target.value)}
                          className="mt-1 w-full rounded-md border border-ink-200 px-3 py-1.5 text-sm"
                        />
                        <p className="mt-1 text-xs text-ink-500">
                          Así verá el archivo el prospecto en WhatsApp.
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <BroadcastVariableMapper
                  variables={variables ?? []}
                  mapping={mapping}
                  onChange={setMapping}
                />

                <div className="mt-4">
                  <BroadcastCostNote
                    estimate={estimate ?? null}
                    category={template.category}
                  />
                </div>

                <div className="mt-4">
                  <TemplatePreview
                    header={template.header}
                    headerMedia={chosenMedia}
                    body={template.body}
                    footer={template.footer}
                    examples={first?.parameters ?? template.bodyExamples}
                  />
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {/* ── Step 2 ─────────────────────────────────────────────────── */}
        {step === 2 ? (
          <div>
            <p className="text-sm text-ink-700">
              Se enviará a los <strong>{eligibleCount}</strong> prospectos que coinciden
              con los filtros actuales.
            </p>
            {/* The only place a human can catch that the board's client-side
                stage filter is, or is not, part of this send. */}
            <BroadcastFilterChips filters={filters} />

            <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-ink-100">
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b border-ink-100 bg-white text-left text-xs uppercase tracking-wide text-ink-500">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Ref</th>
                    <th className="px-3 py-2 font-semibold">Nombre</th>
                    <th className="px-3 py-2 font-semibold">Teléfono</th>
                    <th className="px-3 py-2 font-semibold">Datos</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview?.eligible ?? []).map((r) => (
                    <tr key={r.leadId} className="border-b border-ink-050 last:border-0">
                      <td className="px-3 py-1.5 text-ink-500">{r.sessionRef}</td>
                      <td className="px-3 py-1.5 text-ink-700">{r.name ?? "—"}</td>
                      <td className="px-3 py-1.5 text-ink-700">{r.phoneMasked}</td>
                      <td className="px-3 py-1.5 text-ink-700">
                        {r.parameters.join(" · ") || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(preview?.excluded.length ?? 0) > 0 ? (
              <details className="mt-3 rounded-lg border border-ink-100 p-3">
                <summary className="cursor-pointer text-sm text-ink-700">
                  {preview?.excluded.reduce((n, g) => n + g.count, 0)} prospectos quedan
                  fuera
                </summary>
                <ul className="mt-2 space-y-1 text-sm text-ink-700">
                  {(preview?.excluded ?? []).map((g) => (
                    <li key={g.reason}>
                      <strong>{g.count}</strong>{" "}
                      {BROADCAST_EXCLUSION_LABELS[
                        g.reason as keyof typeof BROADCAST_EXCLUSION_LABELS
                      ] ?? g.reason}
                      <span className="ml-1 text-xs text-ink-500">
                        ({g.sessionRefs.join(", ")})
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        ) : null}

        {/* ── Step 3 ─────────────────────────────────────────────────── */}
        {step === 3 && template ? (
          <div>
            <TemplatePreview
              header={template.header}
              headerMedia={chosenMedia}
              body={template.body}
              footer={template.footer}
              examples={first?.parameters ?? template.bodyExamples}
            />
            {eligibleCount > 1 ? (
              <p className="mt-1 text-xs text-ink-500">
                Así lo verá {first?.name ?? "el primer destinatario"}, y{" "}
                {eligibleCount - 1} personas más con sus propios datos.
              </p>
            ) : null}

            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg border border-ink-100 p-3">
                <p className="text-xs text-ink-500">Destinatarios</p>
                <p className="text-lg font-semibold text-ink-900">{eligibleCount}</p>
              </div>
              <div className="rounded-lg border border-ink-100 p-3">
                <p className="text-xs text-ink-500">Excluidos</p>
                <p className="text-lg font-semibold text-ink-900">
                  {preview?.excluded.reduce((n, g) => n + g.count, 0) ?? 0}
                </p>
              </div>
              <div className="rounded-lg border border-ink-100 p-3">
                <p className="text-xs text-ink-500">Costo estimado</p>
                <p className="text-lg font-semibold text-ink-900">
                  {estimate?.knownCategory
                    ? formatTemplateTotal(estimate.total, estimate.currency)
                    : "—"}
                </p>
              </div>
            </div>

            {/* The tile above is a bare total. This says WHICH of Meta's prices
                it is — the last screen before the money is spent should not be
                the one screen that omits the category. */}
            <div className="mt-3">
              <BroadcastCostNote
                estimate={estimate ?? null}
                category={template.category}
              />
            </div>

            {/* Warning, never a block: the wording of an opt-out line is free
                form, and a rule of ours rejecting a valid Spanish sentence
                would be worse than the warning it replaces. */}
            {template.category === "MARKETING" &&
            !mentionsOptOut(template.body, template.footer) ? (
              <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Esta plantilla no le dice al prospecto cómo darse de baja. Meta lo exige
                para mensajes de marketing.
              </p>
            ) : null}

            <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Meta cobrará por cada mensaje entregado. Puedes detener el envío, pero los
              mensajes ya enviados no se pueden retirar ni reembolsar.
            </p>
            <p className="mt-2 text-xs text-ink-500">
              Los montos son estimaciones con la tarifa de Meta vigente a{" "}
              {estimate?.asOf}. El cobro real lo determina Meta y se consulta en su
              panel.
            </p>
          </div>
        ) : null}

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              if (step === 1) handleClose();
              else setStep((step - 1) as Step);
            }}
            className="rounded-md px-3 py-2 text-sm text-ink-700 hover:bg-ink-050"
          >
            {step === 1 ? "Cancelar" : "Atrás"}
          </button>

          {step < 3 ? (
            <button
              type="button"
              disabled={
                step === 1
                  ? !template || !mappingComplete || !headerSatisfied
                  : previewPending || eligibleCount === 0
              }
              onClick={() => {
                setStep((step + 1) as Step);
              }}
              className="rounded-md bg-ink-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
            >
              Continuar
            </button>
          ) : (
            <button
              type="button"
              disabled={create.isPending || !template || eligibleCount === 0}
              onClick={() => {
                if (!template) return;
                create.mutate(
                  {
                    templateId: template.id,
                    filters,
                    mapping,
                    headerMediaAssetId: chosenMedia?.assetId ?? null,
                    headerMediaFileName:
                      header?.format === "DOCUMENT" && chosenMedia
                        ? headerFileName.trim() || chosenMedia.fileName
                        : null,
                  },
                  {
                    onSuccess: (broadcast) => {
                      toast.success("Envío iniciado.");
                      handleClose();
                      void navigate(`/envios/${broadcast.id}`);
                    },
                  },
                );
              }}
              className="rounded-md bg-ink-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
            >
              {create.isPending
                ? "Enviando…"
                : `Enviar a ${String(eligibleCount)} prospectos`}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
